/**
 * Frontend helpers for Patreon linking and the Admin > Patreon feature table.
 *
 * Linking / unlinking / refresh talk to Cloudflare Pages + the patreon-utility
 * edge function. The matrix is saved from Admin > Patreon; benefits are looked
 * up live from defaults + tier + local overrides.
 */
import supabase from "./Supabase";
import { showToast } from "./Toasts";
import { BenefitsByTier } from "./Benefits";

const PATREON_CLIENT_ID = process.env.REACT_APP_PATREON_CLIENT_ID || null;
const PATREON_REDIRECT_BASE_URL = process.env.REACT_APP_PATREON_REDIRECT_BASE_URL || window.location.origin;
const LOGIN_PATREON_REFRESH_KEY = "scripthive_patreon_refresh_on_login";

/** Set on the login form so getUserInfo will ask Patreon once. Not used on page load. */
export function requestPatreonRefreshOnLogin() {
    try {
        sessionStorage.setItem(LOGIN_PATREON_REFRESH_KEY, "1");
    } catch {
        // Ignore storage failures (private mode, etc.).
    }
}

export function clearPatreonRefreshOnLogin() {
    try {
        sessionStorage.removeItem(LOGIN_PATREON_REFRESH_KEY);
    } catch {
        // Ignore storage failures (private mode, etc.).
    }
}

export function patreonRefreshOnLoginRequested() {
    try {
        return sessionStorage.getItem(LOGIN_PATREON_REFRESH_KEY) === "1";
    } catch {
        return false;
    }
}

// Temp allowlist: only this email can use Patreon linking until the feature is ready.
// Set to null to allow Patreon linking for everyone.
export const PATREON_LINKING_ALLOW_EMAIL: string | null = null;

/** True if this email may see Link Patreon / the "link your account" modal. */
export function isPatreonLinkingAllowed( email?: string | null ): boolean {
    //console.log("isPatreonLinkingAllowed called with email:", email);
    return PATREON_LINKING_ALLOW_EMAIL === null || email === PATREON_LINKING_ALLOW_EMAIL;
}

/** Start OAuth: hand our session to /api/patreon/start, then go to Patreon. */
export async function linkPatreon() {
    if (!PATREON_CLIENT_ID || !PATREON_REDIRECT_BASE_URL) {
        showToast("error", "Patreon integration is not configured.");
        return false;
    }

    const session = await supabase.auth.getSession();
    if (session.data.session?.access_token) {
        const res = await fetch(`${PATREON_REDIRECT_BASE_URL}/api/patreon/start`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ access_token: session.data.session.access_token })
        });
        if (res.ok) {
            const data = await res.json();
            window.location.href = data.redirectUrl;
            return true;
        } else {
            const errorData = await res.json();
            const reason = errorData.message || errorData.error || res.statusText || 'unknown error';
            showToast("error", `Error while linking Patreon account: ${reason}`);
            return false;
        }
    } else {
        showToast("error", "You need to be logged in to do that.");
        return false;
    }
}

/**
 * Unlink Patreon for this user (self or admin).
 * The edge function clears the stored tokens, the Patreon row, and patreon_tier_id.
 * Benefits then fall back to site defaults plus any local overrides.
 */
export async function unlinkPatreon(userId: string) {
    if (!PATREON_CLIENT_ID || !PATREON_REDIRECT_BASE_URL) {
        showToast("error", "Patreon integration is not configured.");
        return null;
    }

    const session = await supabase.auth.getSession();
    const currentUser = session.data.session?.user;

    if (!session.data.session || !currentUser) {
        showToast("error", "You need to be logged in to do that.");
        return null;
    }

    const { data: isAdmin } = await supabase
        .from("userData")
        .select("isApprover")
        .eq("user_id", currentUser?.id)
        .single();

    if (currentUser?.id !== userId && !isAdmin?.isApprover) {
        showToast("error", "Sorry, that isn't allowed.");
        return null;
    }

    const { data: deleteData, error: deleteError } = await supabase.functions.invoke('patreon-utility', {
        body: {
            action: 'delete_tokens',
            user_session: session.data.session?.access_token,
            user_id: userId
        }
    })

    if (!deleteData || deleteError) {
        showToast("error", `Error while unlinking Patreon account: ${deleteError?.message || 'Unknown error'}`);
        return null;
    }
    
    showToast("success", "Successfully unlinked Patreon account.");
    return deleteData as {
        result: string;
        user?: {
            patreon_linked?: boolean;
            patreon_tier_id?: string | null;
        } | null;
    };
}

/** Row from patreon_users (email, tier, status) for the Account page. */
export async function getPatreonUser(userId: string) {
    const { data, error } = await supabase
        .from("patreon_users")
        .select("*")
        .eq("user_id", userId)
        .single();

    if (error) {
        console.error('Error fetching Patreon user data:', error);
        return null;
    }

    return data;
}

/** Ask Patreon again for this user's campaign tier (does not copy benefit values onto the user). */
export async function refreshPatreonUser(userId: string, options?: { silent?: boolean }) {
    const silent = options?.silent === true;
    const session = await supabase.auth.getSession();
    const currentUser = session.data.session?.user;

    if (!session.data.session || !currentUser) {
        if (!silent) {
            showToast("error", "You need to be logged in to do that.");
        }
        return false;
    }

    const { data: isAdmin } = await supabase
        .from("userData")
        .select("isApprover")
        .eq("user_id", currentUser?.id)
        .single();

    if (currentUser?.id !== userId && !isAdmin?.isApprover) {
        if (!silent) {
            showToast("error", "Sorry, that isn't allowed.");
        }
        return false;
    }

    const { data, error } = await supabase.functions.invoke('patreon-utility', {
        body: {
            action: 'refresh_user',
            user_session: session.data.session?.access_token,
            user_id: userId
        }
    });

    if (error) {
        if (!silent) {
            showToast("error", "Unable to refresh Patreon user information.");
        }
        console.error('Error refreshing Patreon user information:', error);
        return null;
    }

    return data;
}

/** How many Script Hive accounts currently have Patreon linked. */
export async function getPatreonUserCount() {
    const { data, error } = await supabase
        .from("patreon_users")
        .select("user_id", { count: "exact" });

    if (error) {
        console.error('Error fetching Patreon user count:', error);
        return null;
    }

    return data?.length || 0;
}

/** One Patreon reward tier, as returned from Patreon. */
export type PatreonCampaignTier = {
    id: string;
    title: string | null;
    amount_cents: number | null;
    description: string | null;
    patron_count: number | null;
    published: boolean | null;
    published_at: string | null;
    unpublished_at: string | null;
    url: string | null;
    discord_role_ids: string[] | null;
};

/** Tiers from Patreon, plus our saved feature settings and old VIP / Platinum lists. */
export type PatreonTiersResponse = {
    result: string;
    campaign_id: string;
    tiers: PatreonCampaignTier[];
    features: BenefitsByTier;
    configured: {
        tier_ids: string[];
        vip_tier_ids: string[];
        platinum_tier_ids: string[];
    };
};

export type PatreonSaveTierFeaturesResponse = {
    result: string;
    features: BenefitsByTier;
};

/** Load Patreon tiers and the feature settings for the admin page (admins only). */
export async function getPatreonCampaignTiers(): Promise<PatreonTiersResponse | null> {
    const session = await supabase.auth.getSession();

    if (!session.data.session?.access_token) {
        showToast("error", "You need to be logged in to do that.");
        return null;
    }

    const { data, error } = await supabase.functions.invoke('patreon-utility', {
        body: {
            action: 'get_tiers',
            user_session: session.data.session.access_token,
        }
    });

    if (error || data?.error) {
        const reason = data?.error || error?.message || 'Unknown error';
        showToast("error", `Unable to fetch Patreon tiers: ${reason}`);
        console.error('Error fetching Patreon campaign tiers:', error || data);
        return null;
    }

    return {
        ...data,
        // If features are missing, start from an empty set and use defaults in the UI.
        features: data.features ?? {},
    } as PatreonTiersResponse;
}

/** Save the Admin > Patreon feature table (admins only). */
export async function savePatreonTierFeatures(
    features: BenefitsByTier,
): Promise<PatreonSaveTierFeaturesResponse | null> {
    const session = await supabase.auth.getSession();

    if (!session.data.session?.access_token) {
        showToast("error", "You need to be logged in to do that.");
        return null;
    }

    const { data, error } = await supabase.functions.invoke('patreon-utility', {
        body: {
            action: 'save_tier_features',
            user_session: session.data.session.access_token,
            features,
        }
    });

    if (error || data?.error) {
        const reason = data?.error || error?.message || 'Unknown error';
        showToast("error", `Unable to save Patreon tier features: ${reason}`);
        console.error('Error saving Patreon tier features:', error || data);
        return null;
    }

    showToast("success", "Patreon tier features saved.");
    return data as PatreonSaveTierFeaturesResponse;
}
